import { useMutation, useQuery } from '@apollo/react-hooks';
import { safeAsync } from '@kargotech/tms-common/Utilities/utils';
import { useCallback, useEffect } from 'react';

import { EVENT_SERVICE_DEVICE_TYPE } from '~/configurations/constants';
import ADD_RECIPIENT_DEVICE_KEY from '~/graphql/event/mutations/addRecipientDeviceKey.event';
import UPDATE_BATCH_IS_READ_TAB_NOTIFICATION from '~/graphql/event/mutations/updateBatchIsReadTabNotification.event';
import UPDATE_IS_READ_TAB_NOTIFICATION from '~/graphql/event/mutations/updateIsReadTabNotification.event';
import NOTIFICATIONS from '~/graphql/event/queries/notifications.event';
import { notifications as formatNotificationRawData } from '~/models/notifications';
import { APOLLO_CLIENTS } from '~/services/apollo';
import { config as firebaseConfig, messaging } from '~/services/firebase';

const useInAppNotification = ({
  companyKsuid = '',
  language = 'id',
}) => {
  const {
    refetch,
    loading,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    data: notificationRawData,
    fetchMore,
  } = useQuery(NOTIFICATIONS, {
    client: APOLLO_CLIENTS.EVENT,
    notifyOnNetworkStatusChange: true,
    variables: {
      companyKsuid,
    },
  });

  const [addRecipientToken] = useMutation(ADD_RECIPIENT_DEVICE_KEY, {
    client: APOLLO_CLIENTS.EVENT,
  });

  const [readNotification] = useMutation(UPDATE_IS_READ_TAB_NOTIFICATION, {
    client: APOLLO_CLIENTS.EVENT,
    variables: {
      companyKsuid,
      inAppNotificationKsuid: undefined,
    },
    onCompleted() {
      refetch();
    },
  });

  const readNotificationByKey = useCallback((key: string) => {
    readNotification({
      variables: {
        companyKsuid,
        inAppNotificationKsuid: key,
      },
    });
  }, [companyKsuid, readNotification]);

  const [readAllNotification] = useMutation(UPDATE_BATCH_IS_READ_TAB_NOTIFICATION, {
    client: APOLLO_CLIENTS.EVENT,
    variables: {
      companyKsuid,
    },
    onCompleted() {
      refetch();
    },
  });

  const notifications = formatNotificationRawData(notificationRawData, language);

  const loadMore = useCallback(() => fetchMore({
    variables: {
      after: notifications.pageInfo.endCursor,
    },
    /* eslint-disable @typescript-eslint/no-unsafe-member-access */
    /* eslint-disable @typescript-eslint/no-unsafe-assignment */
    /* eslint-disable @typescript-eslint/no-unsafe-return */
    updateQuery: (prevResult, { fetchMoreResult }) => ({
      // @ts-ignore: Temporary ignore query result type
      ...fetchMoreResult,
      companyInAppNotifications: {
        // @ts-ignore: Temporary ignore query result type
        ...fetchMoreResult.companyInAppNotifications,
        edges: [
          // @ts-ignore: Temporary ignore query result type
          ...prevResult.companyInAppNotifications.edges,
          // @ts-ignore: Temporary ignore query result type
          ...fetchMoreResult.companyInAppNotifications.edges,
        ],
      },
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
      /* eslint-enable @typescript-eslint/no-unsafe-assignment */
      /* eslint-enable @typescript-eslint/no-unsafe-return */
    }),
  }), [fetchMore, notifications.pageInfo.endCursor]);

  const updateRecipientToken = (token: string) => {
    addRecipientToken({
      variables: {
        deviceToken: token,
        deviceType: EVENT_SERVICE_DEVICE_TYPE,
      },
    });
  };

  const initNotification = async () => {
    if (messaging) {
      safeAsync(async () => {
        await messaging.requestPermission();
        if ('serviceWorker' in navigator) {
          // eslint-disable-next-line max-len
          const swUrl = `${window.location.origin}/firebase-messaging-sw.js?firebaseConfig=${window.encodeURIComponent(
            JSON.stringify(firebaseConfig),
          )}`;
          const registration = await navigator.serviceWorker.register(swUrl);
          messaging.useServiceWorker(registration);
          messaging.onTokenRefresh(updateRecipientToken);
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          messaging.onMessage(refetch);
          const token = await messaging.getToken();
          updateRecipientToken(token);
        }
      });
    }
  };

  useEffect(() => {
    initNotification();
    const onForegroundHandler = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };
    document.addEventListener('visibilitychange', onForegroundHandler);
    return () => {
      document.removeEventListener('visibilitychange', onForegroundHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [
    notifications,
    loading,
    loadMore,
    readNotificationByKey,
    readAllNotification,
  ] as const;
};

export default useInAppNotification;
